// サステナブル画像ファイル名の表示・編集補助をまとめる service です。
export function createSustainableImageService(deps) {
    const {
        PANEL_TYPE,
        SUSTAINABLE_IMAGE_FILE_FIELD,
        normalizeKakousijiUiString,
        readRowValueIgnoreCase,
        isEmpty,
        fnc,
        getEntryLock,
        getLayoutLock,
        entrydataRef,
        getSustainableImageFileInputValue,
        sustainableImageFileCancelBlurRef,
        setSustainableImageFileInputValue,
        setSustainableImageFileEditing,
        loadSustainableImageFromServer,
        rerender,
        // 任意: 画面ごとの編集可否判定(未指定なら entrylock/layoutlock のみで判定)
        isEditBlocked,
        // 任意: 確定後に走らせる追加処理(品番マスタは entrydata の dispatch が必要)
        afterCommit
    } = deps;

    // 128 では画像ファイル名入力欄を通常の BASE 表示から除外します。
    function sustainableDisplayTextFields(source, panelType) {
        if (!Array.isArray(source)) return source;
        const isType128 = String(panelType ?? "") === PANEL_TYPE.SUSTAINABLE_MARK;
        if (!isType128) return source;
        return source.filter((field) => String(field?.columnid ?? "").trim().toUpperCase() !== SUSTAINABLE_IMAGE_FILE_FIELD);
    }

    // サステナブル画像ファイル名を entrydata から読み取ります。
    function readSustainableImageFileName(source) {
        return normalizeKakousijiUiString(readRowValueIgnoreCase(source || {}, SUSTAINABLE_IMAGE_FILE_FIELD));
    }

    // ファイル名入力値が安全かを判定します。
    function isSafeSustainableImageFileNameInput(fileName) {
        const value = normalizeKakousijiUiString(fileName);
        if (value === "") return true;
        if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return false;
        if (/^[a-zA-Z]:/.test(value)) return false;
        if (value.indexOf(":") !== -1) return false;
        if (value.indexOf("/") !== -1 || value.indexOf("\\") !== -1) return false;
        return value.indexOf("..") === -1;
    }

    // 不正な入力値に対する警告を表示します。
    function warnInvalidSustainableImageFileName() {
        const message = "ファイル名のみ入力してください。フルパス、URL、パス区切り、.. は使用できません。";
        if (!isEmpty(fnc.current) && typeof fnc.current.warnmsg === "function") {
            fnc.current.warnmsg(message);
            return;
        }
        if (typeof window !== "undefined" && typeof window.alert === "function") window.alert(message);
    }

    // ファイル名編集を開始します。context は画面固有の編集可否判定に渡します。
    function startSustainableImageFileNameEdit(event, context) {
        if (!isEmpty(event) && typeof event.stopPropagation === "function") event.stopPropagation();
        const blocked = typeof isEditBlocked === "function"
            ? isEditBlocked(context)
            : (getEntryLock() || getLayoutLock());
        if (blocked) return;
        const currentFileName = readSustainableImageFileName(entrydataRef.current);
        sustainableImageFileCancelBlurRef.current = false;
        setSustainableImageFileInputValue(currentFileName);
        setSustainableImageFileEditing(true);
    }

    // ファイル名編集を確定します。
    function commitSustainableImageFileName() {
        if (sustainableImageFileCancelBlurRef.current) {
            sustainableImageFileCancelBlurRef.current = false;
            return;
        }
        const nextFileName = normalizeKakousijiUiString(getSustainableImageFileInputValue());
        if (!isSafeSustainableImageFileNameInput(nextFileName)) {
            warnInvalidSustainableImageFileName();
            return;
        }

        if (isEmpty(entrydataRef.current) || typeof entrydataRef.current !== "object") entrydataRef.current = {};
        entrydataRef.current[SUSTAINABLE_IMAGE_FILE_FIELD] = nextFileName;
        setSustainableImageFileInputValue(nextFileName);
        setSustainableImageFileEditing(false);
        loadSustainableImageFromServer(entrydataRef.current);
        if (!isEmpty(fnc.current) && typeof fnc.current.modify === "function") fnc.current.modify(true);
        if (typeof afterCommit === "function") afterCommit();
        rerender();
    }

    // ファイル名編集をキャンセルします。
    function cancelSustainableImageFileNameEdit() {
        sustainableImageFileCancelBlurRef.current = true;
        setSustainableImageFileInputValue(readSustainableImageFileName(entrydataRef.current));
        setSustainableImageFileEditing(false);
    }

    // サステナブル画像入力欄のキー操作を処理します。
    function handleSustainableImageFileNameKeyDown(event) {
        if (isEmpty(event)) return;
        if (event.key === "Enter") {
            event.preventDefault();
            commitSustainableImageFileName();
            return;
        }
        if (event.key === "Escape") {
            event.preventDefault();
            cancelSustainableImageFileNameEdit();
        }
    }

    // サステナブル画像パネル押下時に編集へ切り替えます。
    function handleSustainableImagePanelClick(event, context) {
        startSustainableImageFileNameEdit(event, context);
    }

    return {
        sustainableDisplayTextFields,
        readSustainableImageFileName,
        isSafeSustainableImageFileNameInput,
        warnInvalidSustainableImageFileName,
        startSustainableImageFileNameEdit,
        commitSustainableImageFileName,
        cancelSustainableImageFileNameEdit,
        handleSustainableImageFileNameKeyDown,
        handleSustainableImagePanelClick
    };
}
